package br.com.texeratx.boardgametimeapp.ui.screens.players.edit

import br.com.texeratx.boardgametimeapp.core.i18n.LanguageManager
import br.com.texeratx.boardgametimeapp.core.image.PickedImage
import br.com.texeratx.boardgametimeapp.core.utils.apiToUiDate
import br.com.texeratx.boardgametimeapp.data.model.player.UpdatePlayerRequest
import br.com.texeratx.boardgametimeapp.domain.usecases.player.GetPlayerDetailUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.player.UpdatePlayerUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.upload.UploadPlayerImageUseCase
import br.com.texeratx.boardgametimeapp.ui.utils.uiToApiDate
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch

class EditPlayerViewModel(
    private val getPlayerDetail: GetPlayerDetailUseCase,
    private val updatePlayer: UpdatePlayerUseCase,
    private val uploadPlayerImageUseCase: UploadPlayerImageUseCase,
    private val lang: LanguageManager
) : ScreenModel {

    val state = MutableStateFlow(EditPlayerState())

    private var pendingImage: PickedImage? = null
    private var loadedPlayerId: Long? = null

    fun updateState(reducer: (EditPlayerState) -> EditPlayerState) {
        state.value = reducer(state.value)
    }

    fun load(playerId: Long) {
        if (loadedPlayerId == playerId && state.value.name.isNotBlank()) return
        loadedPlayerId = playerId

        screenModelScope.launch {
            updateState { it.copy(isSaving = false, error = null) }

            getPlayerDetail(playerId).fold(
                onSuccess = { player ->
                    pendingImage = null
                    state.value = EditPlayerState(
                        name = player.name,
                        nickname = player.nickname.orEmpty(),
                        imageUrl = player.imageUrl.orEmpty(),
                        birthday = player.birthday?.apiToUiDate().orEmpty(),
                        phone = player.phone.orEmpty()
                    )
                },
                onFailure = { e ->
                    updateState { it.copy(error = e.message ?: lang.strings.value.errorLoadingPlayers) }
                }
            )
        }
    }

    fun onImageSelected(image: PickedImage) {
        pendingImage = image
        updateState {
            it.copy(
                selectedImageName = image.fileName,
                localImageBytes = image.bytes,
                localImageMimeType = image.mimeType
            )
        }
    }

    fun clearLocalImage() {
        pendingImage = null
        updateState {
            it.copy(
                selectedImageName = null,
                localImageBytes = null,
                localImageMimeType = null
            )
        }
    }

    fun save(playerId: Long, onSuccess: () -> Unit) {
        screenModelScope.launch {
            val s = state.value

            if (s.name.isBlank()) {
                updateState { it.copy(error = lang.strings.value.playerNameRequired) }
                return@launch
            }

            val apiBirthday = uiToApiDate(s.birthday)
            if (s.birthday.isNotBlank() && apiBirthday == null) {
                updateState { it.copy(error = lang.strings.value.invalidBirthday) }
                return@launch
            }

            updateState { it.copy(isSaving = true, error = null) }

            // ✅ 1) upload se houver imagem nova
            var finalImageUrl: String? = s.imageUrl.ifBlank { null }
            val pending = pendingImage

            if (pending != null) {
                updateState { it.copy(isUploadingImage = true) }

                val uploadResult = uploadPlayerImageUseCase(
                    bytes = pending.bytes,
                    fileName = pending.fileName,
                    mimeType = pending.mimeType
                )

                uploadResult.fold(
                    onSuccess = { url ->
                        finalImageUrl = url
                        updateState { it.copy(imageUrl = url, isUploadingImage = false) }
                    },
                    onFailure = { e ->
                        updateState {
                            it.copy(
                                isUploadingImage = false,
                                isSaving = false,
                                error = e.message ?: lang.strings.value.errorUploadingImage
                            )
                        }
                        return@launch
                    }
                )
            }

            // ✅ 2) update player
            val result = updatePlayer(
                playerId,
                UpdatePlayerRequest(
                    name = s.name,
                    nickname = s.nickname.ifBlank { null },
                    imageUrl = finalImageUrl,
                    birthday = apiBirthday,
                    phone = s.phone.ifBlank { null }
                )
            )

            result.fold(
                onSuccess = {
                    updateState { it.copy(isSaving = false) }
                    onSuccess()
                },
                onFailure = { e ->
                    updateState {
                        it.copy(isSaving = false, error = e.message ?: lang.strings.value.errorSavingPlayer)
                    }
                }
            )
        }
    }
}
